import { useEffect, useState } from "react";
import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { ToastContainer } from "react-toastify";

// --- ICONS (SVG) ---
const SearchIcon = () => (
  <svg
    className="w-5 h-5 text-gray-400"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);
const PlusIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 4v16m8-8H4"
    />
  </svg>
);
const EditIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);
const TrashIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);
const XIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);
const ChevronLeft = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 19l-7-7 7-7"
    />
  </svg>
);
const ChevronRight = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5l7 7-7 7"
    />
  </svg>
);

const THEME_BLUE = "#4A9CF0";
const ITEMS_PER_PAGE = 5;

export default function CategoryCRUD() {
  // --- STATE ---
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [name, setName] = useState("");
  const [keywordInput, setKeywordInput] = useState("");
  const [keywords, setKeywords] = useState([]);
  const [editId, setEditId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const [sortBy, setSortBy] = useState("createdAt");
  const [order, setOrder] = useState("desc");

  /* ================= KEYWORDS LOGIC ================= */
  const addKeyword = () => {
    const val = keywordInput.trim();
    if (!val || keywords.includes(val)) return;
    setKeywords([...keywords, val]);
    setKeywordInput("");
  };

  const removeKeyword = (index) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };

  /* ================= API CALLS ================= */
  const getCategories = async (pageNo = page) => {
    try {
      setLoading(true);
      const res = await makeRequestAuth("post", "/admin/getCategoryList", {
        search,
        pageNo,
        size: ITEMS_PER_PAGE,
        sortBy,
        order,
      });

      if (res?.error) {
        showErrorToast(res.message || "Failed to fetch categories");
        setCategories([]);
        return;
      }

      setCategories(res.data.list || []);
      setTotalPages(res.data.pagination.totalPages || 1);
      setPage(pageNo);
    } catch (err) {
      showErrorToast("Something went wrong while fetching categories");
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const createCategory = async () => {
    try {
      const res = await makeRequestAuth("post", "/admin/createCategory", {
        name,
        keywords,
      });

      if (res?.error) {
        showErrorToast(res.message || "Failed to create category");
        return;
      }

      showSuccessToast("Category created successfully");
      closeModal();
      getCategories(1);
    } catch (err) {
      showErrorToast(
        err.message || "Something went wrong while creating category",
      );
    }
  };

  const updateCategory = async () => {
    try {
      const res = await makeRequestAuth("post", "/admin/updateCategory", {
        id: editId,
        name,
        keywords,
      });

      if (res?.error) {
        showErrorToast(res.message || "Failed to update category");
        return;
      }

      showSuccessToast("Category updated successfully");
      closeModal();
      getCategories(page);
    } catch (err) {
      showErrorToast(
        err.message || "Something went wrong while updating category",
      );
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Are you sure you want to delete this category?"))
      return;

    try {
      const res = await makeRequestAuth("post", "/admin/deleteCategory", {
        id,
      });

      if (res?.error) {
        showErrorToast(res.message || "Failed to delete category");
        return;
      }

      showSuccessToast("Category deleted successfully");
      getCategories(page);
    } catch (err) {
      showErrorToast(
        err.message || "Something went wrong while deleting category",
      );
    }
  };

  /* ================= HANDLERS ================= */
  const handleSubmit = () => {
    if (!name.trim()) {
      showErrorToast("Category name is required");
      return;
    }
    editId ? updateCategory() : createCategory();
  };

  const handleEdit = (cat) => {
    setEditId(cat._id);
    setName(cat.name);
    setKeywords(cat.keywords || []);
    setKeywordInput("");
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setEditId(null);
    setName("");
    setKeywords([]);
    setKeywordInput("");
  };

  useEffect(() => {
    getCategories(1);
  }, [search, sortBy, order]);

  return (
    <div className="p-4">
      <ToastContainer />

      <div className="space-y-6">
        {/* === HEADER SECTION === */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
              Categories
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage your product categories and keywords.
            </p>
          </div>
          <button
            onClick={() => setOpenModal(true)}
            className="w-full md:w-auto flex justify-center items-center gap-2 px-5 py-2.5 rounded-lg shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 text-white font-medium"
            style={{ backgroundColor: THEME_BLUE }}
          >
            <PlusIcon />
            <span>Add Category</span>
          </button>
        </div>

        {/* === FILTERS BAR (RESPONSIVE) === */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col lg:flex-row gap-4 justify-between items-center">
          {/* Search Input - Full width on mobile */}
          <div className="relative w-full lg:w-96">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SearchIcon />
            </div>
            <input
              placeholder="Search by category name..."
              className="pl-10 pr-4 py-2.5 w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all"
              style={{ "--tw-ring-color": `${THEME_BLUE}40` }}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>

          {/* Sort Controls - Stacked on mobile, row on desktop */}
          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <select
              className="w-full sm:w-auto px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:outline-none focus:ring-2"
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setPage(1);
              }}
            >
              <option value="createdAt">Date Created</option>
              <option value="name">Name (A-Z)</option>
            </select>

            <select
              className="w-full sm:w-auto px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:outline-none focus:ring-2"
              value={order}
              onChange={(e) => {
                setOrder(e.target.value);
                setPage(1);
              }}
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
        </div>

        {/* === DATA TABLE / LIST (RESPONSIVE) === */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Header - Hidden on Mobile, Visible on Desktop */}
          <div className="hidden md:grid grid-cols-12 gap-4 bg-gray-50/50 p-4 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <div className="col-span-3">Category Name</div>
            <div className="col-span-7">Keywords</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {/* Body */}
          <div className="divide-y divide-gray-50">
            {loading ? (
              <div className="p-10 text-center text-gray-400">
                Loading categories...
              </div>
            ) : categories.length === 0 ? (
              <div className="p-10 text-center flex flex-col items-center justify-center text-gray-400">
                <div className="bg-gray-100 p-4 rounded-full mb-3">
                  <SearchIcon />
                </div>
                <p>No categories found.</p>
              </div>
            ) : (
              categories.map((cat) => (
                <div
                  key={cat._id}
                  className="
                    flex flex-col p-4 gap-3                 
                    md:grid md:grid-cols-12 md:gap-4 md:items-center 
                    hover:bg-gray-50 transition-colors duration-150
                  "
                >
                  {/* Name: Big on mobile, Column on desktop */}
                  <div className="md:col-span-3">
                    <span className="md:hidden text-xs text-gray-400 uppercase font-bold tracking-wider mb-1 block">
                      Category
                    </span>
                    <div className="font-medium text-gray-900 text-lg md:text-base truncate">
                      {cat.name}
                    </div>
                  </div>

                  {/* Keywords: Stacked block on mobile, Column on desktop */}
                  <div className="md:col-span-7">
                    <span className="md:hidden text-xs text-gray-400 uppercase font-bold tracking-wider mb-2 block">
                      Keywords
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {(cat.keywords || []).length > 0 ? (
                        cat.keywords.map((k, i) => (
                          <span
                            key={i}
                            className="px-2.5 py-1 rounded-full text-xs font-medium border"
                            style={{
                              backgroundColor: `${THEME_BLUE}10`,
                              color: THEME_BLUE,
                              borderColor: `${THEME_BLUE}20`,
                            }}
                          >
                            {k}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-300 text-sm italic">
                          No keywords
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions: Bottom row on mobile, Right align on desktop */}
                  <div className="md:col-span-2 flex md:justify-end gap-2 mt-2 md:mt-0 border-t md:border-t-0 pt-3 md:pt-0">
                    <button
                      onClick={() => handleEdit(cat)}
                      className="flex-1 md:flex-none flex justify-center items-center gap-2 md:gap-0 p-2 rounded-lg text-gray-600 bg-gray-50 md:bg-transparent hover:bg-blue-50 hover:text-blue-600 transition-colors border md:border-none"
                    >
                      <EditIcon />
                      <span className="md:hidden text-sm font-medium">
                        Edit
                      </span>
                    </button>
                    <button
                      onClick={() => deleteCategory(cat._id)}
                      className="flex-1 md:flex-none flex justify-center items-center gap-2 md:gap-0 p-2 rounded-lg text-gray-600 bg-gray-50 md:bg-transparent hover:bg-red-50 hover:text-red-600 transition-colors border md:border-none"
                    >
                      <TrashIcon />
                      <span className="md:hidden text-sm font-medium">
                        Delete
                      </span>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* === PAGINATION === */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-6 pb-6">
            <button
              disabled={page === 1}
              onClick={() => getCategories(page - 1)}
              className="p-2 rounded-lg border border-gray-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronLeft />
            </button>

            <div className="flex gap-1 overflow-x-auto">
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => getCategories(i + 1)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                    page === i + 1
                      ? "text-white shadow-md"
                      : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                  }`}
                  style={page === i + 1 ? { backgroundColor: THEME_BLUE } : {}}
                >
                  {i + 1}
                </button>
              ))}
            </div>

            <button
              disabled={page === totalPages}
              onClick={() => getCategories(page + 1)}
              className="p-2 rounded-lg border border-gray-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronRight />
            </button>
          </div>
        )}
      </div>

      {/* === MODAL === */}
      {openModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <h2 className="text-lg font-bold text-gray-800">
                {editId ? "Update Category" : "Create New Category"}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XIcon />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500 uppercase">
                  Category Name
                </label>
                <input
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                  style={{ "--tw-ring-color": `${THEME_BLUE}40` }}
                  placeholder="e.g., Electronics"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500 uppercase">
                  Keywords (SEO)
                </label>
                <div className="flex gap-2">
                  <input
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                    style={{ "--tw-ring-color": `${THEME_BLUE}40` }}
                    placeholder="Type and press Enter"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addKeyword()}
                  />
                  <button
                    onClick={addKeyword}
                    className="px-4 py-2 rounded-lg text-white font-medium hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: THEME_BLUE }}
                  >
                    Add
                  </button>
                </div>
              </div>

              <div className="min-h-[80px] p-3 bg-gray-50 rounded-lg border border-dashed border-gray-300 flex flex-wrap content-start gap-2">
                {keywords.length === 0 && (
                  <span className="text-gray-400 text-sm w-full text-center mt-4">
                    No keywords added yet.
                  </span>
                )}
                {keywords.map((k, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full text-sm font-medium bg-white border shadow-sm"
                    style={{
                      color: THEME_BLUE,
                      borderColor: `${THEME_BLUE}30`,
                    }}
                  >
                    {k}
                    <button
                      onClick={() => removeKeyword(i)}
                      className="p-1 rounded-full hover:bg-red-50 hover:text-red-500 transition-colors"
                    >
                      <XIcon />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-6 py-2.5 text-sm font-medium text-white rounded-lg shadow-sm hover:shadow hover:opacity-90 transition-all"
                style={{ backgroundColor: THEME_BLUE }}
              >
                {editId ? "Save Changes" : "Create Category"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
